import { useCallback, useEffect, useRef, useState } from "react";
import logger from "../lib/logger";
import { api } from "../lib/api";
import { getSessionId, getRefFromUrl, saveLastStep, loadLastStep } from "../lib/session";
import { trackOnce, captureUtmFirstTouch } from "../lib/analytics";

export const STEPS = {
    WELCOME: "welcome",
    ONBOARDING: "onboarding",
    RATING: "rating",
    ANALYZING: "analyzing",
    REVEAL: "reveal",
    EMAIL_CAPTURE: "email_capture",
    PAYWALL: "paywall",
    PAYWALL_DISCOUNT: "paywall_discount",
    REFERRAL: "referral",
    UNLOCKED: "unlocked",
    DEAD_END: "dead_end",
    PROFILE_BUILDER: "profile_builder",
    MATCHES: "matches",
    DATING_MATCHES: "dating_matches",
    DATING_MATCH: "dating_match",
    NOTIFICATIONS: "notifications",
    PUBLIC_PROFILE: "public_profile",
    POST_PURCHASE: "post_purchase",
    MEMBERSHIP: "membership",
    AI_COACH: "ai_coach",
    AI_CONCIERGE: "ai_concierge",
};

/**
 * Owns end-user onboarding state machine.
 * Returns step + flow state + handlers; UI rendering lives in StepRenderer.
 */
export function useOnboardingFlow() {
    const [step, setStep] = useState(STEPS.WELCOME);
    // Capture the persisted step synchronously BEFORE the save-effect below overwrites it.
    const [initialLastStep] = useState(() => loadLastStep());
    const [user, setUser] = useState(null);
    const [archetype, setArchetype] = useState(null);
    const [idealPartner, setIdealPartner] = useState(null);
    const [paywallSeen, setPaywallSeen] = useState(false);
    const [bootError, setBootError] = useState(null);
    const [viewingProfileId, setViewingProfileId] = useState(null);
    const [viewingMatchId, setViewingMatchId] = useState(null);
    const [foundingMemberNumber, setFoundingMemberNumber] = useState(null);
    const popNavRef = useRef(false);
    const firstStepRef = useRef(true);

    // Browser/phone back support: each step becomes a history entry.
    useEffect(() => {
        if (!step) return;
        if (popNavRef.current) { popNavRef.current = false; return; }
        try {
            if (firstStepRef.current) {
                firstStepRef.current = false;
                window.history.replaceState({ eaStep: step }, "");
            } else if (window.history.state?.eaStep !== step) {
                window.history.pushState({ eaStep: step }, "");
            }
        } catch { /* ignore history API failures */ }
    }, [step]);

    useEffect(() => {
        const onPop = (e) => {
            const s = e.state?.eaStep;
            if (s && Object.values(STEPS).includes(s)) {
                popNavRef.current = true;
                setStep(s);
            }
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    // ----- Bootstrap user + restore last step -----
    useEffect(() => {
        (async () => {
            try {
                captureUtmFirstTouch();
                const sid = getSessionId();
                const refCode = getRefFromUrl();
                const u = await api.createUser(sid, refCode || null);
                setUser(u);
                trackOnce("landed");

                if (u.disqualified) {
                    setStep(STEPS.DEAD_END);
                    return;
                }
                if (u.archetype_id) {
                    const arch = await api.getArchetype(u.archetype_id);
                    setArchetype(arch);
                    try {
                        const ideal = await api.getArchetype(arch.ideal_partner_id);
                        setIdealPartner(ideal);
                    } catch (err) {
                        logger.warn("ideal partner archetype fetch failed:", err);
                    }
                    setStep(u.unlocked ? STEPS.UNLOCKED : STEPS.REVEAL);
                    return;
                }
                const last = initialLastStep;
                if (last && Object.values(STEPS).includes(last) && last !== STEPS.UNLOCKED) {
                    setStep(last);
                }
            } catch (e) {
                logger.error(e);
                setBootError("Could not connect. Please refresh.");
            }
        })();
         
    }, []);

    // Persist current step for resume-on-reload
    useEffect(() => { saveLastStep(step); }, [step]);

    // Poll for referral-driven unlock while on REFERRAL screen
    useEffect(() => {
        if (step !== STEPS.REFERRAL || !user) return;
        const id = setInterval(async () => {
            try {
                const u = await api.getUser(user.session_id);
                setUser(u);
                if (u.unlocked) setStep(STEPS.UNLOCKED);
            } catch (err) {
                logger.warn("referral poll failed:", err);
            }
        }, 4000);
        return () => clearInterval(id);
    }, [step, user]);

    // ----- Handlers -----
    const onboardingDone = useCallback(async (answers) => {
        try {
            const updated = await api.submitScore(user.session_id, answers);
            setUser(updated);
            if (updated.disqualified) {
                setStep(STEPS.DEAD_END);
                return;
            }
            const arch = await api.getArchetype(updated.archetype_id);
            setArchetype(arch);
            try {
                const ideal = await api.getArchetype(arch.ideal_partner_id);
                setIdealPartner(ideal);
            } catch (err) {
                logger.warn("ideal partner archetype fetch failed:", err);
            }
            setStep(STEPS.RATING);
        } catch (e) {
            logger.error("submitScore failed:", e);
            const { toast } = await import("sonner");
            toast.error("We couldn't save your answers. Please check your connection and tap Reveal again — your progress is saved.");
        }
    }, [user]);

    const handleCheckoutSuccess = useCallback(async (plan, acceptedTerms = true) => {
        try {
            const result = await api.mockCheckout(user.session_id, plan, acceptedTerms);
            const u = await api.getUser(user.session_id);
            setUser(u);
            setFoundingMemberNumber(result?.founding_member_number || u.founding_member_number || null);
            setStep(STEPS.POST_PURCHASE);
        } catch (e) {
            logger.error("checkout failed:", e);
        }
    }, [user]);

    const handlePaidUnlock = useCallback(async (number) => {
        try {
            const u = await api.getUser(user.session_id);
            setUser(u);
            setFoundingMemberNumber(number || u.founding_member_number || null);
            setStep(STEPS.POST_PURCHASE);
        } catch (e) {
            logger.error("post-payment refresh failed:", e);
        }
    }, [user]);

    const handleCodeRedeemed = useCallback(async () => {
        try {
            const u = await api.getUser(user.session_id);
            setUser(u);
            setStep(STEPS.UNLOCKED);
        } catch (e) {
            logger.error("post-redeem refresh failed:", e);
        }
    }, [user]);

    const dismissPaywall = useCallback(() => {
        if (!paywallSeen) {
            setPaywallSeen(true);
            setStep(STEPS.PAYWALL_DISCOUNT);
        } else {
            setStep(STEPS.REVEAL);
        }
    }, [paywallSeen]);

    const handleReferralUnlocked = useCallback(async () => {
        try {
            const u = await api.getUser(user.session_id);
            setUser(u);
            if (u.unlocked) setStep(STEPS.UNLOCKED);
        } catch (err) {
            logger.warn("referral unlock refresh failed:", err);
        }
    }, [user]);

    const refreshUser = useCallback(async () => {
        try {
            const u = await api.getUser(user.session_id);
            setUser(u);
        } catch (err) {
            logger.warn("refreshUser failed:", err);
        }
    }, [user]);

    return {
        // state
        step, user, archetype, idealPartner, bootError, viewingProfileId, viewingMatchId, foundingMemberNumber,
        // setters
        setStep, setViewingProfileId, setViewingMatchId, setUser, handlePaidUnlock,
        // handlers
        onboardingDone, handleCheckoutSuccess, handleCodeRedeemed, dismissPaywall, handleReferralUnlocked, refreshUser,
    };
}
